import type { GeoContentPlan, GeoPlanPeriod, GeoReport } from "../api/geoService";

/**
 * 1011 算法备案:境外 AI 平台(GEO 探针里的 ChatGPT)。租户 `domesticOnly=true` 时报告里不展示。
 * ai 侧本批起 domesticOnly 会跳过 GPT 探针;这里兜底**旧报告**(已含 gpt 行)与灰度期混合数据。
 */
export const FOREIGN_GEO_ENGINES: ReadonlySet<string> = new Set(["gpt", "chatgpt", "openai"]);

export function isForeignEngine(name: string | null | undefined): boolean {
  return !!name && FOREIGN_GEO_ENGINES.has(name.toLowerCase());
}

const keep = (name: string | null | undefined) => !isForeignEngine(name);

function stripPeriods(ps: GeoPlanPeriod[] | undefined): GeoPlanPeriod[] | undefined {
  return ps?.map((p) => ({
    ...p,
    articles: p.articles.map((a) => ({
      ...a,
      affects_platforms: (a.affects_platforms ?? []).filter(keep),
    })),
  }));
}

/**
 * 去掉报告里所有按平台分的 ChatGPT 行(探针 / 平台分 / 平台详情 / 搜索总览 / 引擎归因 / 内容计划影响平台)。
 * 综合分等**已算好的总分不重算**(ai 侧算的,前端不改口径)。纯函数,不改入参。
 */
export function stripForeignEngines(report: GeoReport): GeoReport {
  const out: GeoReport = {
    ...report,
    platform_scores: (report.platform_scores ?? []).filter((p) => keep(p.platform)),
  };
  if (report.probes) out.probes = report.probes.filter((p) => keep(p.platform));
  const pd = report.platform_details;
  if (pd?.platforms) {
    const platforms = pd.platforms.filter((p) => keep(p.platform));
    out.platform_details = {
      ...pd,
      platforms,
      real_count: platforms.filter((p) => p.provenance === "real").length,
      derived_count: platforms.filter((p) => p.provenance === "derived").length,
    };
  }
  const so = report.search_overview;
  if (so) {
    out.search_overview = {
      ...so,
      ...(so.engines ? { engines: so.engines.filter(keep) } : {}),
      ...(so.samples ? { samples: so.samples.filter((s) => keep(s.platform)) } : {}),
    };
  }
  const si = report.search_insight;
  if (si) {
    out.search_insight = {
      ...si,
      ...(si.engines ? { engines: si.engines.filter((e) => keep(e.name)) } : {}),
      ...(si.engine_attribution
        ? { engine_attribution: si.engine_attribution.filter((e) => keep(e.engine)) }
        : {}),
    };
  }
  const cp: GeoContentPlan | undefined = report.content_plan;
  if (cp) {
    out.content_plan = { ...cp, months: stripPeriods(cp.months), weeks: stripPeriods(cp.weeks) };
  }
  return out;
}
